import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekRangeFilter } from './ui/week-filter';
import i18n from '../i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Download, Filter, Briefcase, Users, Layers, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hours/minutes, never tenths of an hour — "2h 30m" reads instantly on site.
const fmt = (h) => {
  const n = Number(h) || 0;
  const sign = n < 0 ? '−' : '';
  const total = Math.round(Math.abs(n) * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  if (hh && mm) return `${sign}${hh}h ${mm}m`;
  if (hh) return `${sign}${hh}h`;
  return `${sign}${mm}m`;
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const CATEGORY_OPTIONS = [
  { value: '', label: i18n.t('auto.lbl.allCategories') },
  { value: 'standard', label: i18n.t('auto.lbl.standard') },
  { value: 'extra', label: i18n.t('auto.lbl.extraOutOfScope') },
  { value: 'rework', label: i18n.t('auto.lbl.rework') },
];

const TaskReportsTab = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    project_id: '',
    category: '',
    employee_id: '',
    trade: '',
    date_from: '',
    date_to: '',
  });
  const [summary, setSummary] = useState(null);
  const [byType, setByType] = useState([]);
  const [byEmployee, setByEmployee] = useState([]);
  const [view, setView] = useState('summary');
  const [trades, setTrades] = useState([]);
  const [sortBy, setSortBy] = useState('sort_order');

  useEffect(() => {
    (async () => {
      try {
        const [p, e, s] = await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/employees`),
          axios.get(`${API}/settings`),
        ]);
        setProjects((p.data || []).filter((x) => !x.is_archived));
        setEmployees(e.data || []);
        setTrades(s.data?.task_trades || []);
      } catch {}
    })();
  }, []);

  const buildParams = useCallback((extras = {}) => {
    const p = { ...extras };
    if (filters.project_id) p.project_id = filters.project_id;
    if (filters.category) p.category = filters.category;
    if (filters.trade) p.trade = filters.trade;
    if (filters.employee_id) p.employee_id = filters.employee_id;
    if (filters.date_from) p.date_from = new Date(filters.date_from).toISOString();
    if (filters.date_to) p.date_to = new Date(filters.date_to).toISOString();
    return p;
  }, [filters]);

  const apply = async () => {
    try {
      if (view === 'summary') {
        if (!filters.project_id) { setSummary(null); toast.message('Pick a project to see summary'); return; }
        const r = await axios.get(`${API}/task-reports/project-summary`, { params: buildParams() });
        setSummary(r.data);
      } else if (view === 'by-type') {
        const r = await axios.get(`${API}/task-reports/by-task-type`, { params: buildParams() });
        setByType(r.data || []);
      } else if (view === 'by-employee') {
        const r = await axios.get(`${API}/task-reports/by-employee`, { params: buildParams() });
        setByEmployee(r.data || []);
      }
    } catch {
      toast.error(t('auto.misc.failedToLoadReport'));
    }
  };

  useEffect(() => { apply(); /* eslint-disable-next-line */ }, [view, filters.project_id, filters.category, filters.trade, filters.employee_id, filters.date_from, filters.date_to]);

  const exportFile = async (format = 'xlsx') => {
    try {
      const params = { sort_by: sortBy };
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.category) params.category = filters.category;
      if (filters.trade) params.trade = filters.trade;
      if (filters.employee_id) params.employee_id = filters.employee_id;
      if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
      if (filters.date_to) params.date_to = new Date(filters.date_to).toISOString();
      const url = format === 'pdf' ? 'task-reports/export/pdf' : 'task-reports/export/excel';
      const r = await axios.get(`${API}/${url}`, { params, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      const proj = projects.find((p) => p.id === filters.project_id);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const fn = ['task_report', filters.category, proj ? proj.name.replace(/\W+/g, '_') : null].filter(Boolean).join('_') + '.' + ext;
      a.download = weekFileName(fn);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(t('auto.misc.exported'));
    } catch { toast.error(t('auto.misc.exportFailed')); }
  };

  const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e.name || e.full_name || 'Unknown'])), [employees]);

  return (
    <div className="space-y-4" data-testid="task-reports-tab">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4 text-[#AA0000]" />
            {t('auto.misc.filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div>
              <Label className="text-xs">{t('auto.misc.project')}</Label>
              <Select value={filters.project_id || '__all__'} onValueChange={(v) => setFilters(prev => ({ ...prev, project_id: v === '__all__' ? '' : v  }))}>
                <SelectTrigger data-testid="reports-project-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('auto.misc.allProjects')}</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc.category')}</Label>
              <Select value={filters.category || '__all__'} onValueChange={(v) => setFilters(prev => ({ ...prev, category: v === '__all__' ? '' : v  }))}>
                <SelectTrigger data-testid="reports-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value || '__all__'} value={c.value || '__all__'}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc.trade')}</Label>
              <Select value={filters.trade || '__all__'} onValueChange={(v) => setFilters(prev => ({ ...prev, trade: v === '__all__' ? '' : v  }))}>
                <SelectTrigger data-testid="reports-trade-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('auto.misc.allTrades')}</SelectItem>
                  {trades.map((t) => <SelectItem key={t} value={t}>{cap(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc.employee')}</Label>
              <Select value={filters.employee_id || '__all__'} onValueChange={(v) => setFilters(prev => ({ ...prev, employee_id: v === '__all__' ? '' : v  }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('auto.misc.allEmployees')}</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc.dateFrom')}</Label>
              <Input type="date" className="min-w-0 pr-2" value={filters.date_from} onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value  }))} />
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc.dateTo')}</Label>
              <Input type="date" className="min-w-0 pr-2" value={filters.date_to} onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value  }))} />
            </div>
            <div>
              <Label className="text-xs">{t('weekFilter.week')}</Label>
              <div><WeekRangeFilter from={filters.date_from} to={filters.date_to} onChange={(f, t2) => setFilters(prev => ({ ...prev, date_from: f, date_to: t2 }))} testId="task-reports-week-filter" /></div>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2 mt-3 justify-between">
            <div>
              <Label className="text-xs">{t('auto.misc.sortByExport')}</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-44" data-testid="reports-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sort_order">{t('auto.misc.defaultOrder')}</SelectItem>
                  <SelectItem value="trade">{t('auto.misc.trade')}</SelectItem>
                  <SelectItem value="planned_start">{t('auto.misc.plannedStart')}</SelectItem>
                  <SelectItem value="name">{t('auto.misc.taskName')}</SelectItem>
                  <SelectItem value="category">{t('auto.misc.category')}</SelectItem>
                  <SelectItem value="actual_hours">{t('auto.misc.actualHoursDesc')}</SelectItem>
                  <SelectItem value="variance">{t('auto.misc.varianceDesc')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <DocLangToggle testId="doc-lang-taskreportstab-0" />
              <Button onClick={() => exportFile('pdf')} variant="outline" data-testid="export-tasks-report-pdf-btn">
                <FileText className="h-4 w-4 mr-1" />{t('auto.misc.exportPdf')}
              </Button>
              <Button onClick={() => exportFile('xlsx')} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="export-tasks-report-btn">
                <Download className="h-4 w-4 mr-1" />{t('auto.misc.exportExcel')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="summary" className="flex items-center gap-1" data-testid="tab-summary"><Briefcase className="h-3 w-3" />{t('auto.misc.projectSummary')}</TabsTrigger>
          <TabsTrigger value="by-type" className="flex items-center gap-1" data-testid="tab-by-type"><Layers className="h-3 w-3" />{t('auto.misc.byTaskType')}</TabsTrigger>
          <TabsTrigger value="by-employee" className="flex items-center gap-1" data-testid="tab-by-employee"><Users className="h-3 w-3" />{t('auto.misc.byEmployee')}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          {!summary ? (
            <Card><CardContent className="p-6 text-center text-gray-500">{t('auto.misc.pickAProjectToSeeIts')}</CardContent></Card>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label={t('auto.misc.tasks')} value={summary.totals.n_tasks} />
                <Stat label={t('auto.misc.completed')} value={summary.totals.n_completed} />
                <Stat label={t('auto.misc.estTime')} value={fmt(summary.totals.est_hours)} />
                <Stat label={t('auto.misc.actualTime')} value={fmt(summary.totals.actual_hours)} />
                <Stat label="Variance" value={`${summary.totals.variance_hours >= 0 ? '+' : ''}${fmt(summary.totals.variance_hours)} (${summary.totals.variance_pct}%)`}
                      valueClass={summary.totals.variance_hours > 0 ? 'text-[#AA0000]' : summary.totals.variance_hours < 0 ? 'text-green-700' : ''} />
              </div>

              {summary.top_delays?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base text-[#AA0000]">Top Delays (&gt;10% over estimate)</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b"><tr>
                        <th className="text-left p-2">{t('auto.misc.task')}</th>
                        <th className="text-right p-2">{t('auto.misc.estTime')}</th>
                        <th className="text-right p-2">{t('auto.misc.actualTime')}</th>
                        <th className="text-right p-2">{t('auto.misc.variance')}</th>
                      </tr></thead>
                      <tbody>
                        {summary.top_delays.map((d) => (
                          <tr key={d.task_id} className="border-b">
                            <td className="p-2">{d.name}</td>
                            <td className="p-2 text-right tabular-nums">{fmt(d.est_hours)}</td>
                            <td className="p-2 text-right tabular-nums">{fmt(d.actual_hours)}</td>
                            <td className="p-2 text-right tabular-nums text-[#AA0000] font-semibold">+{fmt(d.variance_hours)} ({d.variance_pct}%)</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">{t('auto.misc.tasks')}</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>
                      <th className="text-left p-2">{t('auto.misc.task')}</th>
                      <th className="text-left p-2">{t('auto.misc.category')}</th>
                      <th className="text-right p-2">{t('auto.misc.est')}</th>
                      <th className="text-right p-2">{t('auto.misc.actual')}</th>
                      <th className="text-right p-2">{t('auto.misc.var')}</th>
                      <th className="text-left p-2">{t('auto.misc.status')}</th>
                    </tr></thead>
                    <tbody>
                      {summary.tasks.map((t) => (
                        <tr key={t.id} className="border-b">
                          <td className="p-2">{t.name}</td>
                          <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${t.category === 'extra' ? 'bg-orange-100 text-orange-700' : t.category === 'rework' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{t.category}</span></td>
                          <td className="p-2 text-right tabular-nums">{fmt(t.est_hours)}</td>
                          <td className="p-2 text-right tabular-nums">{fmt(t.actual_hours)}</td>
                          <td className={`p-2 text-right tabular-nums ${(t.variance_hours || 0) > 0 ? 'text-[#AA0000]' : (t.variance_hours || 0) < 0 ? 'text-green-700' : ''}`}>
                            {(t.variance_hours || 0) >= 0 ? '+' : ''}{fmt(t.variance_hours || 0)}
                          </td>
                          <td className="p-2 text-xs">{t.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="by-type">
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-left p-2">{t('auto.misc.taskType')}</th>
                <th className="text-right p-2">{t('auto.misc.projects')}</th>
                <th className="text-right p-2">{t('auto.misc.avgEst')}</th>
                <th className="text-right p-2">{t('auto.misc.avgActual')}</th>
                <th className="text-right p-2">{t('auto.misc.min')}</th>
                <th className="text-right p-2">{t('auto.misc.max')}</th>
                <th className="text-right p-2">{t('auto.misc.variance2')}</th>
              </tr></thead>
              <tbody>
                {byType.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-500">{t('auto.misc.noData')}</td></tr>}
                {byType.map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2 text-right tabular-nums">{r.n_projects}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(r.avg_est_hours)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(r.avg_actual_hours)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(r.min_actual_hours)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(r.max_actual_hours)}</td>
                    <td className={`p-2 text-right tabular-nums ${(r.variance_pct || 0) > 0 ? 'text-[#AA0000]' : (r.variance_pct || 0) < 0 ? 'text-green-700' : ''}`}>
                      {r.variance_pct == null ? '—' : `${r.variance_pct}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="by-employee">
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-left p-2">{t('auto.misc.employee')}</th>
                <th className="text-left p-2">{t('auto.misc.department')}</th>
                <th className="text-right p-2">{t('auto.misc.tasks2')}</th>
                <th className="text-right p-2">{t('auto.misc.totalTime')}</th>
                <th className="text-right p-2">{t('auto.misc.standard')}</th>
                <th className="text-right p-2">{t('auto.misc.extra')}</th>
                <th className="text-right p-2">{t('auto.misc.rework')}</th>
              </tr></thead>
              <tbody>
                {byEmployee.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-500">{t('auto.misc.noData')}</td></tr>}
                {byEmployee.map((r) => (
                  <tr key={r.employee_id} className="border-b">
                    <td className="p-2 font-medium">{r.employee_name || empMap[r.employee_id] || r.employee_id}</td>
                    <td className="p-2 text-gray-600">{r.department || '—'}</td>
                    <td className="p-2 text-right tabular-nums">{r.n_tasks}</td>
                    <td className="p-2 text-right tabular-nums font-semibold">{fmt(r.total_hours)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(r.by_category?.standard)}</td>
                    <td className="p-2 text-right tabular-nums text-orange-700">{fmt(r.by_category?.extra)}</td>
                    <td className="p-2 text-right tabular-nums text-red-700">{fmt(r.by_category?.rework)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Stat = ({ label, value, valueClass = '' }) => (
  <Card><CardContent className="p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${valueClass}`}>{value}</div>
  </CardContent></Card>
);

export default TaskReportsTab;
